// Metadatos de notificaciones COMPARTIDOS entre `pages/Notificaciones.tsx`
// (bandeja de alertas) y `components/perfil/NotificacionesPanel.tsx` (ruteo
// por nivel de urgencia + urgencia por tipo, Perfil). Antes vivían duplicados
// en Notificaciones.tsx; se extrajeron aquí (Task 10) para no divergir.
//
// Los defaults (`DEFAULT_TIPO_URGENCIA`, `DEFAULT_NIVEL_CANALES`) son un
// ESPEJO de `arktickets/domain/notify.py` (fuente de verdad real, puro,
// testeado). Si cambian ahí, actualízalos aquí también.

export type NivelUrgencia = "urgente" | "normal" | "informativa";

// Orden de despliegue: de lo más urgente a lo más informativo.
export const ORDEN_URGENCIA = ["urgente", "normal", "informativa"] as const;

export const CANALES_EXTERNOS = ["push", "telegram"] as const;
export type CanalExterno = (typeof CANALES_EXTERNOS)[number];

// Ícono + etiqueta + color por tipo de alerta. Sustituye al viejo TIPO_HINT
// (que solo traía texto explicativo en un tooltip): el tipo se ve de un
// vistazo, sin pasar el cursor.
export const TIPO_META: Record<string, { icono: string; label: string; color: string }> = {
  mencion: { icono: "💬", label: "Mención", color: "text-violet-300" },
  escalacion: { icono: "🔥", label: "Escalación", color: "text-red-300" },
  quemado: { icono: "🔥", label: "Quemado", color: "text-red-300" },
  por_quemarse: { icono: "⏳", label: "Por quemarse", color: "text-orange-300" },
  recordatorio: { icono: "⏰", label: "Recordatorio", color: "text-amber-300" },
  asignacion: { icono: "📌", label: "Asignación", color: "text-sky-300" },
  nuevo_correo: { icono: "✉️", label: "Nuevo correo", color: "text-cyan-300" },
  buzon_pendiente: { icono: "📥", label: "Buzón", color: "text-cyan-300" },
  cierre: { icono: "✅", label: "Cierre", color: "text-emerald-300" },
  sistema: { icono: "⚙️", label: "Sistema", color: "text-zinc-400" },
};

// Fallback para un tipo que no esté en el mapa (no debería pasar, pero un
// enum nuevo del backend no debe romper el render).
export const TIPO_META_DEFAULT = { icono: "🔔", label: "Alerta", color: "text-zinc-400" };

export function metaDeTipo(tipo: string) {
  return TIPO_META[tipo] ?? TIPO_META_DEFAULT;
}

// Modo enfoque (Task 12): opciones rápidas para iniciar + formateador de
// hora, compartidos entre el botón del Layout y la fila del panel de Perfil
// (mismo motivo que el resto de este archivo: no divergir).
export const ENFOQUE_OPCIONES: Array<{ minutos: number; label: string }> = [
  { minutos: 30, label: "30 min" },
  { minutos: 60, label: "1 hora" },
  { minutos: 120, label: "2 horas" },
];

// Textos del modo enfoque — UNA sola redacción para el botón del Layout (activo
// e inactivo), la fila de Perfil y su Ayuda. Antes eran cuatro parafraseos y
// uno decía «push» (jerga) donde la Ayuda dice «navegador».
export const ENFOQUE_TEXTOS = {
  /** Qué hace el botón 🎯 (lo usa la Ayuda de Mi perfil; ya no es tooltip). */
  titulo: "Modo enfoque: silencia los avisos del navegador y de Telegram un rato",
  /** Encabezado del menú cuando está inactivo (le sigue la rejilla de duraciones). */
  pregunta: "Silenciar navegador y Telegram por…",
  /** Qué sí y qué no se silencia. Igual activo que inactivo. */
  detalle:
    "La campanita sigue recibiendo todo y al terminar te llega un resumen. Tus recordatorios sí te avisan.",
} as const;

export function formatHoraEnfoque(iso: string): string {
  return new Date(iso).toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Título + color del "punto" de acento por nivel de urgencia (bandeja +
// encabezados del ruteo en Perfil).
export const URGENCIA_META: Record<NivelUrgencia, { titulo: string; punto: string }> = {
  urgente: { titulo: "🔴 Atiende ya", punto: "bg-red-500" },
  normal: { titulo: "🟡 Pendientes", punto: "bg-amber-500" },
  informativa: { titulo: "⚪ Para enterarte", punto: "bg-zinc-500" },
};

// Solo el emoji del nivel, para selectores compactos (segmentado por tipo).
export const NIVEL_ICONO: Record<NivelUrgencia, string> = {
  urgente: "🔴",
  normal: "🟡",
  informativa: "⚪",
};

// Espejo de `arktickets/domain/notify.py::DEFAULT_TIPO_URGENCIA` — nivel
// efectivo de un tipo cuando el usuario no tiene override.
export const DEFAULT_TIPO_URGENCIA: Record<string, NivelUrgencia> = {
  mencion: "urgente",
  escalacion: "urgente",
  quemado: "urgente",
  recordatorio: "urgente",
  asignacion: "normal",
  nuevo_correo: "normal",
  buzon_pendiente: "normal",
  por_quemarse: "normal",
  cierre: "informativa",
  sistema: "informativa",
};

// Espejo de `arktickets/domain/notify.py::DEFAULT_NIVEL_CANALES` — canales
// externos default por nivel cuando el usuario no tiene override (in-app
// SIEMPRE va, no se lista aquí).
export const DEFAULT_NIVEL_CANALES: Record<NivelUrgencia, CanalExterno[]> = {
  urgente: ["push", "telegram"],
  normal: ["push"],
  informativa: [],
};

/** Nivel de urgencia efectivo de un tipo: override del usuario > default. */
export function nivelDeTipo(
  tipo: string,
  overrides: Record<string, string>,
): NivelUrgencia {
  const o = overrides[tipo];
  if (o === "urgente" || o === "normal" || o === "informativa") return o;
  return DEFAULT_TIPO_URGENCIA[tipo] ?? "normal";
}

/** Canales externos efectivos de un nivel: override del usuario > default. */
export function canalesDeNivel(
  nivel: NivelUrgencia,
  overrides: Record<string, string[]>,
): CanalExterno[] {
  const o = overrides[nivel];
  if (!o) return DEFAULT_NIVEL_CANALES[nivel];
  return CANALES_EXTERNOS.filter((c) => o.includes(c));
}
